import {
    AmountRoundingLevel,
    RuleActionType,
    RuleConditionAmount,
    RuleConditionAmountRounding,
    RuleConditionType,
    RuleSource,
    TransactionRuleActionDefinition,
    TransactionRuleConditionDefinition,
} from "../types/transactionRule";
import { CounterpartyDirection } from "./counterparties";
import {
    isRuleConditionSimilarTo,
    parseSimilarToValue,
} from "./split-aware-description.helper";

export const DEFAULT_DIRECTION_FOR_COUNTERPARTY_CONDITION =
    CounterpartyDirection.VENDOR;

/**
 * Whether a rule should be shown on the Rules page / rule picker based on its
 * live "currently matching" count. NORMALIZATION (AI) rules are hidden when they
 * currently match zero transactions; every other rule source is always visible
 * and never reads the map. Lives in `common` (unlike the frontend
 * `settings/Rules/utils.ts`) so it can be unit-tested from `dist`.
 *
 * Takes a single count map (not the applications-based `transactionCounts`) to
 * avoid the two-same-typed-args footgun: AI-rule visibility must be driven by
 * `matchingCounts`, since applications rows drift stale-low (PRO-9305).
 */
export function isRuleVisibleByMatchingCount(
    rule: { id: string; source: RuleSource | null },
    matchingCounts: Record<string, number>,
): boolean {
    return (
        rule.source !== RuleSource.NORMALIZATION ||
        (matchingCounts[rule.id] ?? 0) > 0
    );
}

export interface SimilarToRuleForExposure {
    id: string;
    source: RuleSource | null;
    conditions: Array<
        Pick<TransactionRuleConditionDefinition, "conditionType" | "value">
    >;
}

export interface CollectExposedSimilarToDescriptionIdsParams {
    rules: SimilarToRuleForExposure[];
    matchingCounts: Record<string, number>;
    mergedToCanonicalMap?: Record<string, string>;
}

/**
 * The normalized-description clusters reachable through a rule the user can
 * actually see, i.e. the SIMILAR_TO targets of the rules `isRuleVisibleByMatchingCount`
 * keeps. Rule exposure is deliberately gated on a live matching count (the full
 * AI-rule list runs into the thousands — PRO-9305), so surfaces that offer
 * clusters as a choice must be gated the same way (PRO-11195).
 *
 * `mergedToCanonicalMap` remaps a rule still pointing at a merged-away
 * description onto its canonical cluster head.
 */
export function collectExposedSimilarToDescriptionIds({
    rules,
    matchingCounts,
    mergedToCanonicalMap,
}: CollectExposedSimilarToDescriptionIdsParams): Set<string> {
    const exposedIds = new Set<string>();

    for (const rule of rules) {
        if (!isRuleVisibleByMatchingCount(rule, matchingCounts)) {
            continue;
        }

        for (const condition of rule.conditions) {
            if (condition.conditionType !== RuleConditionType.SIMILAR_TO) {
                continue;
            }
            const { value } = condition;
            if (typeof value !== "string" && !isRuleConditionSimilarTo(value)) {
                continue;
            }
            const { normalizedDescriptionId } = parseSimilarToValue(value);
            exposedIds.add(
                mergedToCanonicalMap?.[normalizedDescriptionId] ??
                    normalizedDescriptionId,
            );
        }
    }

    return exposedIds;
}

export interface SplitAction {
    isPercentage?: boolean;
    autoConfirm?: boolean;
    splits: Array<{
        categoryId?: string;
        amount?: number | null;
        entityId?: number;
        splitDescription?: string;
        classIds?: string[];
        counterpartyId?: string | null;
        accountId?: string | null;
        accrualAccountId?: string | null;
        scheduleTemplateId?: string | null;
    }>;
}

export const PERSONAL_CATEGORY_BUSINESS_ENTITY_ERROR =
    "Category type must match the entity. A personal category cannot be combined with a business entity, and a business category cannot be combined with a personal entity.";

export const SPLIT_CATEGORY_ENTITY_MISMATCH_ERROR =
    "Category type must match the entity.";

export const GL_ACCOUNT_REQUIRED_FOR_SPLIT_ERROR =
    "A cash ledger account is required for each split in this workspace.";

/**
 * Checks whether every split line has a general ledger account assigned.
 * Used by GL-first workspaces, where each split must classify by a GL account.
 */
export function hasSplitMissingAccount(
    splits: ReadonlyArray<{ accountId?: string | null }>,
): boolean {
    return splits.some((split) => !split.accountId);
}

interface CategoryEntityConflictParams {
    isCategoryPersonal: boolean;
    isEntityBusiness: boolean;
}

/**
 * Checks whether a split row pairs a personal category with a business entity
 * or a business category with a personal entity.
 */
export function hasCategoryEntityConflict({
    isCategoryPersonal,
    isEntityBusiness,
}: CategoryEntityConflictParams): boolean {
    return isCategoryPersonal === isEntityBusiness;
}

interface SplitCategoryEntityConflictParams {
    splits: ReadonlyArray<{ categoryId?: string; entityId?: number }>;
    isCategoryPersonal: (categoryId: string) => boolean | undefined;
    isEntityBusiness: (entityId: number) => boolean | undefined;
}

/**
 * Checks whether any split row pairs a category and entity of opposite types.
 */
export function hasSplitCategoryEntityConflict({
    splits,
    isCategoryPersonal,
    isEntityBusiness,
}: SplitCategoryEntityConflictParams): boolean {
    return splits.some((split) => {
        if (!split.categoryId || !split.entityId) {
            return false;
        }
        const catPersonal = isCategoryPersonal(split.categoryId);
        const entBusiness = isEntityBusiness(split.entityId);
        if (catPersonal === undefined || entBusiness === undefined) {
            return false;
        }
        return hasCategoryEntityConflict({
            isCategoryPersonal: catPersonal,
            isEntityBusiness: entBusiness,
        });
    });
}

const ROUNDING_DIVISORS: Record<AmountRoundingLevel, number> = {
    [AmountRoundingLevel.DOLLAR]: 1,
    [AmountRoundingLevel.TEN]: 10,
    [AmountRoundingLevel.HUNDRED]: 100,
    [AmountRoundingLevel.THOUSAND]: 1000,
};

export function isAmountRoundingLevel(
    value: unknown,
): value is AmountRoundingLevel {
    return (
        typeof value === "string" &&
        Object.values(AmountRoundingLevel).includes(
            value as AmountRoundingLevel,
        )
    );
}

export function getRoundingDivisor(level: AmountRoundingLevel): number {
    return ROUNDING_DIVISORS[level];
}

function isRuleConditionAmount(value: unknown): value is RuleConditionAmount {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isRuleConditionAmountRounding(
    value: unknown,
): value is RuleConditionAmountRounding {
    return (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        "level" in value &&
        "isCredit" in value
    );
}

export function isAmountCashInFromCondition(condition: {
    conditionType: string;
    value: unknown;
}): boolean {
    if (
        condition.conditionType === RuleConditionType.AMOUNT_IS_ROUND ||
        condition.conditionType === RuleConditionType.AMOUNT_IS_NOT_ROUND
    ) {
        if (isRuleConditionAmountRounding(condition.value)) {
            return condition.value.isCredit;
        }
        return true;
    }
    if (!isRuleConditionAmount(condition.value)) {
        return true;
    }
    return (
        (typeof condition.value.min === "number" && condition.value.min >= 0) ||
        (typeof condition.value.max === "number" && condition.value.max > 0)
    );
}

/**
 * Extracts the numeric day-count from a `DATE_PROXIMITY` rule condition.
 * Returns `undefined` when the condition is absent or its value is not a
 * positive integer (which can happen if legacy data is malformed).
 */
export function getDateProximityDays(
    condition: { conditionType: string; value: unknown } | undefined,
): number | undefined {
    if (
        condition === undefined ||
        condition.conditionType !== RuleConditionType.DATE_PROXIMITY ||
        typeof condition.value !== "number" ||
        !Number.isFinite(condition.value) ||
        !Number.isInteger(condition.value) ||
        condition.value <= 0
    ) {
        return undefined;
    }

    return condition.value;
}

export function groupAmountConditionsByDirection<
    T extends { conditionType: string; value: unknown },
>(conditions: T[]): T[][] {
    const cashInConditions = conditions.filter((c) =>
        isAmountCashInFromCondition(c),
    );
    const cashOutConditions = conditions.filter(
        (c) => !isAmountCashInFromCondition(c),
    );
    return [cashInConditions, cashOutConditions].filter((g) => g.length > 0);
}

interface ClassifiableCondition {
    conditionType: RuleConditionType | string;
    value: unknown;
}

export interface ClassifiableAction {
    actionType: RuleActionType | string;
    value: unknown;
}

export interface ClassifiableRule {
    conditions: ClassifiableCondition[];
    actions: ClassifiableAction[];
}

export interface AccountIdentity {
    name: string;
    type: string;
    subtype: string;
}

export type CategoryGlMappingState =
    | "default"
    | "simple-uniform"
    | "simple-divergent"
    | "advanced";

export type ResolveAccountIdentity = (
    action: ClassifiableAction,
    entityId: number,
) => AccountIdentity | null;

export type RuleDirection = "in" | "out";

const RULE_DIRECTIONS: readonly RuleDirection[] = ["in", "out"];

const ALLOWED_CONDITION_TYPES: readonly string[] = [
    RuleConditionType.ENTITY,
    RuleConditionType.CATEGORY,
    RuleConditionType.AMOUNT,
];

const SINGLE_ACCOUNT_ACTION_TYPES: readonly string[] = [
    RuleActionType.ACCOUNT_ID,
    RuleActionType.ACCOUNT_NAME,
];

export function isDirectionOnlyAmount(value: unknown): RuleDirection | null {
    if (!isRuleConditionAmount(value)) {
        return null;
    }
    const isCashIn = value.min === 0 && value.max === undefined;
    const isCashOut = value.max === 0 && value.min === undefined;
    if (isCashIn) {
        return "in";
    }
    if (isCashOut) {
        return "out";
    }
    return null;
}

// The business a rule's account actions resolve in: the last ENTITY action
// when the rule moves the transaction, otherwise the transaction's own.
interface ResolveRuleTargetEntityIdParams {
    actions: ReadonlyArray<
        Pick<TransactionRuleActionDefinition, "actionType" | "value">
    >;
    transactionEntityId: number;
}

export function resolveRuleTargetEntityId({
    actions,
    transactionEntityId,
}: ResolveRuleTargetEntityIdParams): number {
    return actions.reduce((targetEntityId, action) => {
        if (action.actionType !== RuleActionType.ENTITY || !action.value) {
            return targetEntityId;
        }
        const actionEntityId = Number(action.value);
        return Number.isFinite(actionEntityId)
            ? actionEntityId
            : targetEntityId;
    }, transactionEntityId);
}

export function getRuleEntityIds(rule: ClassifiableRule): number[] | null {
    const entityCondition = rule.conditions.find(
        (condition) => condition.conditionType === RuleConditionType.ENTITY,
    );
    if (!entityCondition || !Array.isArray(entityCondition.value)) {
        return null;
    }
    return entityCondition.value.map((id) => Number(id));
}

export function getRuleDirections(
    rule: ClassifiableRule,
): RuleDirection[] | null {
    const amountConditions = rule.conditions.filter(
        (condition) => condition.conditionType === RuleConditionType.AMOUNT,
    );
    if (amountConditions.length === 0) {
        return [...RULE_DIRECTIONS];
    }
    if (amountConditions.length > 1) {
        return null;
    }
    const direction = isDirectionOnlyAmount(amountConditions[0].value);
    return direction ? [direction] : null;
}

/**
 * The direction a transaction falls in for rule matching / voting: a positive
 * amount is cash `in`, everything else (including `0`) is cash `out`. Mirrors
 * the sign check used for prompt selection in categorization.
 */
export function getTransactionDirection(amount: number): RuleDirection {
    return amount > 0 ? "in" : "out";
}

/**
 * The single `AMOUNT` condition value that encodes a direction: `{min: 0}` for
 * cash in, `{max: 0}` for cash out. The frontend renders these as "Cash in" /
 * "Cash out" tags with no number.
 */
export function buildDirectionAmountConditionValue(
    direction: RuleDirection,
): RuleConditionAmount {
    return direction === "in" ? { min: 0 } : { max: 0 };
}

/**
 * A legacy directionless AI rule: it carries no direction `AMOUNT` condition, so
 * it matches both signs and needs conversion before it can be direction-scoped.
 */
export function isLegacyDirectionlessRule(rule: ClassifiableRule): boolean {
    return getRuleDirections(rule)?.length === 2;
}

export function getSingleAccountAction(
    rule: ClassifiableRule,
): ClassifiableAction | null {
    if (rule.actions.length !== 1) {
        return null;
    }
    const [action] = rule.actions;
    if (!SINGLE_ACCOUNT_ACTION_TYPES.includes(action.actionType)) {
        return null;
    }
    if (
        action.actionType === RuleActionType.ACCOUNT_ID &&
        Array.isArray(action.value)
    ) {
        return null;
    }
    return action;
}

function isSingleCategoryConditionValue(value: unknown): boolean {
    if (Array.isArray(value)) {
        return new Set(value).size === 1;
    }
    return value !== null && value !== undefined;
}

function hasOnlyAllowedConditions(rule: ClassifiableRule): boolean {
    return rule.conditions.every((condition) => {
        if (condition.conditionType === RuleConditionType.AMOUNT) {
            return isDirectionOnlyAmount(condition.value) !== null;
        }
        if (condition.conditionType === RuleConditionType.CATEGORY) {
            return isSingleCategoryConditionValue(condition.value);
        }
        return ALLOWED_CONDITION_TYPES.includes(condition.conditionType);
    });
}

/**
 * A simple accounting rule maps a category to a single account for one or more
 * (entity, direction) pairs using only entity/category/direction conditions.
 * These are the rules the category GL-mapping editor owns end to end.
 */
export function isSimpleAccountingRule(rule: ClassifiableRule): boolean {
    return (
        hasOnlyAllowedConditions(rule) &&
        getSingleAccountAction(rule) !== null &&
        (getRuleEntityIds(rule)?.length ?? 0) > 0 &&
        getRuleDirections(rule) !== null
    );
}

export interface SimpleRuleAssignment {
    direction: RuleDirection;
    entityId: number;
    action: ClassifiableAction;
}

export function collectSimpleAssignments(
    rules: ClassifiableRule[],
): SimpleRuleAssignment[] | null {
    const assignments: SimpleRuleAssignment[] = [];
    const seenDirectionEntity = new Set<string>();

    for (const rule of rules) {
        if (!hasOnlyAllowedConditions(rule)) {
            return null;
        }
        const action = getSingleAccountAction(rule);
        const entityIds = getRuleEntityIds(rule);
        const directions = getRuleDirections(rule);
        if (!action || !entityIds || entityIds.length === 0 || !directions) {
            return null;
        }
        for (const direction of directions) {
            for (const entityId of entityIds) {
                const key = `${direction}:${entityId}`;
                if (seenDirectionEntity.has(key)) {
                    return null;
                }
                seenDirectionEntity.add(key);
                assignments.push({ direction, entityId, action });
            }
        }
    }

    return assignments;
}

interface IsUniformForDirectionParams {
    assignments: SimpleRuleAssignment[];
    direction: RuleDirection;
    resolveAccountIdentity: ResolveAccountIdentity;
}

function isUniformForDirection({
    assignments,
    direction,
    resolveAccountIdentity,
}: IsUniformForDirectionParams): boolean {
    const directionAssignments = assignments.filter(
        (assignment) => assignment.direction === direction,
    );
    if (directionAssignments.length === 0) {
        return true;
    }

    let reference: AccountIdentity | null = null;
    for (const assignment of directionAssignments) {
        const identity = resolveAccountIdentity(
            assignment.action,
            assignment.entityId,
        );
        if (!identity) {
            return false;
        }
        if (!reference) {
            reference = identity;
            continue;
        }
        if (
            reference.name !== identity.name ||
            reference.type !== identity.type ||
            reference.subtype !== identity.subtype
        ) {
            return false;
        }
    }

    return true;
}

/**
 * Classifies how a category's matched accounting rules drive its GL posting.
 * The account resolver is injected so the helper stays pure and reusable by
 * both backend (ledger-backed) and frontend (client-side) callers.
 */
export interface ClassifyCategoryGlMappingParams {
    rules: ClassifiableRule[];
    resolveAccountIdentity: ResolveAccountIdentity;
}

export function classifyCategoryGlMapping({
    rules,
    resolveAccountIdentity,
}: ClassifyCategoryGlMappingParams): CategoryGlMappingState {
    if (rules.length === 0) {
        return "default";
    }

    const assignments = collectSimpleAssignments(rules);
    if (!assignments) {
        return "advanced";
    }

    const isUniform = RULE_DIRECTIONS.every((direction) =>
        isUniformForDirection({
            assignments,
            direction,
            resolveAccountIdentity,
        }),
    );

    return isUniform ? "simple-uniform" : "simple-divergent";
}

/**
 * Matches a rule against a set of category ids via either a CATEGORY condition
 * (scalar or array) or a CATEGORY action (scalar).
 */
export function ruleMatchesCategory(
    rule: ClassifiableRule,
    categoryIdSet: Set<string>,
): boolean {
    const conditionMatches = rule.conditions.some((condition) => {
        if (condition.conditionType !== RuleConditionType.CATEGORY) {
            return false;
        }
        return Array.isArray(condition.value)
            ? condition.value.some((id) => categoryIdSet.has(String(id)))
            : categoryIdSet.has(String(condition.value));
    });
    const actionMatches = rule.actions.some(
        (action) =>
            action.actionType === RuleActionType.CATEGORY &&
            typeof action.value === "string" &&
            categoryIdSet.has(action.value),
    );
    return conditionMatches || actionMatches;
}

export interface SplitActionFE {
    isPercentage?: boolean;
    autoConfirm?: boolean;
    splits: Array<{
        categoryId?: string;
        amount?: number | null;
        percentageAmount?: number | null;
        entityId?: number;
        splitDescription?: string;
        classIds?: string[];
        counterpartyId?: string | null;
        accountId?: string | null;
        accrualAccountId?: string | null;
        scheduleTemplateId?: string | null;
    }>;
}
